/**
 * Launch schedule for the listing request feature (P-4972). Both instants
 * carry an explicit Europe/Berlin offset so they mean the same moment in
 * every viewer's timezone - the browser's local timezone never shifts them.
 * The offset differs per date because summer time ends on 2026-10-25:
 * +02:00 (CEST) for the launch, +01:00 (CET) for the banner end. Changing
 * either date is a deploy, on purpose.
 *
 * The comparisons run against the viewer's own clock, so a device whose clock
 * is badly off sees the feature early or late. That is accepted: the backend
 * is live already and an early request lands in the same procurement queue as
 * any other. Do not read these dates as a hard cutoff - there is no
 * server-side date guard behind them.
 *
 * Exported as milliseconds next to the readable ISO literals: parsing a
 * constant string does not belong in a render path, while Date.now() has to
 * stay there (see useListingRequestEnabled).
 */

/**
 * The feature goes live for customers at this instant. Before it, no entry
 * point is rendered for customers - launch banner, search and product list
 * CTAs, the user menu link and the /my-listing-requests route. Logged-in
 * employees see everything earlier, for end-to-end testing in production
 * (useListingRequestEnabled). From this instant on it is live for everyone.
 */
export const LISTING_REQUEST_LAUNCH_AT = '2026-09-01T08:00:00+02:00';
export const LISTING_REQUEST_LAUNCH_AT_MS = new Date(
  LISTING_REQUEST_LAUNCH_AT
).getTime();

/**
 * Exclusive end of the launch banner: business-wise the banner runs until
 * 2026-11-20 23:59 Europe/Berlin INCLUSIVE, so this constant is the first
 * instant it is gone - 2026-11-21 00:00. The date accompanies the feature for
 * about three months and clears the shop header before Black Week (starts
 * 2026-11-23). The feature itself stays reachable through search and the
 * product lists afterwards.
 */
export const LISTING_REQUEST_BANNER_END_AT = '2026-11-21T00:00:00+01:00';
export const LISTING_REQUEST_BANNER_END_AT_MS = new Date(
  LISTING_REQUEST_BANNER_END_AT
).getTime();

export type ListingRequestType = 'brand' | 'product';

export type ListingRequestSource = 'search' | 'category' | 'banner';

export type ListingRequestStatus =
  | 'received'
  | 'under_review'
  | 'being_listed'
  | 'not_being_listed'
  | 'available'
  /** Handed over to Customer Service - the listing process itself is over. */
  | 'closed';

export type ListingRequestStatusVariant =
  | 'neutral'
  | 'pending'
  | 'positive'
  | 'negative';

export interface IListingRequest {
  id: number;
  type: ListingRequestType;
  brand_name: string;
  product_name: string;
  created: string;
  /** Kept as string: a status added in the ERP must not break this page. */
  status: ListingRequestStatus | string;
  /** Only set on available requests: the name the listing was actually created under. */
  linked_name?: string;
  /**
   * Shop-relative product list URL for that listing, built server-side. Missing while
   * the listing is not synced into the shop yet - then the row stays plain text.
   */
  linked_path?: string;
}

/**
 * The customer's own requests. Every reader uses this path and key, so the page,
 * the dashboard panel and the tile teaser share one react-query cache entry, and
 * the request modal invalidates exactly that entry after a submit.
 */
export const LISTING_REQUESTS_MINE_PATH =
  '/api/onlineshop/listing-requests/mine';
export const LISTING_REQUESTS_MINE_QUERY_KEY = 'listing-requests-mine';

export interface IAssortmentMatch {
  type: ListingRequestType;
  name: string;
  brand_name: string;
  url: string;
}

export interface IAssortmentMatchResult {
  matches: IAssortmentMatch[];
  /** True only on a normalized name equality - the only case that blocks a request. */
  exact: boolean;
}

export interface IListingRequestPayload {
  type: ListingRequestType;
  source: ListingRequestSource;
  brand_name: string;
  product_name: string;
  description: string;
  quantity_per_month: string;
  consent: boolean;
  /**
   * When the customer confirmed that none of the suggested matches is what they
   * are looking for - ISO 8601 with the browser's own offset, so procurement
   * reads the moment the customer saw the list, not a UTC translation of it.
   * Null whenever no matches were shown.
   */
  match_confirmed_at: string | null;
  /** The matches that were on screen at that moment; empty when there were none. */
  match_suggestions: IAssortmentMatch[];
}

const STATUS_LABELS: Record<ListingRequestStatus, string> = {
  received: 'Received',
  under_review: 'Under review',
  being_listed: 'Being listed',
  not_being_listed: 'Not being listed',
  available: 'Available',
  closed: 'Closed',
};

const STATUS_VARIANTS: Record<
  ListingRequestStatus,
  ListingRequestStatusVariant
> = {
  received: 'neutral',
  under_review: 'pending',
  being_listed: 'positive',
  not_being_listed: 'negative',
  available: 'positive',
  // Neither a win nor a refusal: Customer Service took the case over.
  closed: 'neutral',
};

export function listingRequestStatusLabel(status: string): string {
  return STATUS_LABELS[status as ListingRequestStatus] ?? status;
}

export function listingRequestStatusVariant(
  status: string
): ListingRequestStatusVariant {
  return STATUS_VARIANTS[status as ListingRequestStatus] ?? 'neutral';
}
